import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import { tokens } from "../../theme";
import { PageHeader } from "../../components/PageHeader";
import {
  foyerApi,
  type OwnerTeamInvite,
  type OwnerTeamMember,
} from "../../http/foyer";
import { friendlyError } from "../../utils/apiError";

/**
 * The organisation's own people (`CONTENT-23`).
 *
 * A content owner is an organisation, not a user. Nenda creates the
 * organisation and appoints its team admin; the team admin invites and removes
 * colleagues here, without asking Nenda.
 *
 * Everything that decides SCOPE happens on the server: the invite is bound to
 * this session's organisation and carries the standard owner rights, whatever
 * this page sends. The page only asks for an email.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function refusalText(e: unknown): string {
  const code = (e as { code?: string }).code;
  switch (code) {
    case "user_already_exists":
      return "That email already has a login.";
    case "invite_already_pending":
      return "An invite to that email is already waiting to be accepted.";
    case "invalid_email":
      return "That does not look like an email address.";
    case "cannot_remove_self":
      return "You cannot remove your own login.";
    case "member_is_team_admin":
      return "Team admins are managed by Nenda. Contact support to change who administers your team.";
    default:
      return friendlyError(e);
  }
}

export default function SupplierTeamPage() {
  const [members, setMembers] = useState<OwnerTeamMember[]>([]);
  const [invites, setInvites] = useState<OwnerTeamInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sentLink, setSentLink] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const team = await foyerApi.getOwnerTeam();
      setMembers(team.members);
      setInvites(team.invites);
      setError(null);
    } catch (e) {
      setError(refusalText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async () => {
    setSending(true);
    setError(null);
    setSentLink(null);
    try {
      const created = await foyerApi.inviteOwnerColleague(email.trim());
      setEmail("");
      setSentLink(created.acceptUrl ?? null);
      await load();
    } catch (e) {
      setError(refusalText(e));
    } finally {
      setSending(false);
    }
  };

  const withdraw = async (inv: OwnerTeamInvite) => {
    if (!window.confirm(`Withdraw the invite to ${inv.email}?`)) return;
    try {
      await foyerApi.withdrawOwnerInvite(inv.id);
      await load();
    } catch (e) {
      setError(refusalText(e));
    }
  };

  const remove = async (m: OwnerTeamMember) => {
    if (
      !window.confirm(
        `Remove ${m.email}? They lose access to your titles and numbers immediately.`
      )
    ) {
      return;
    }
    try {
      await foyerApi.removeOwnerColleague(m.id);
      await load();
    } catch (e) {
      setError(refusalText(e));
    }
  };

  return (
    <Box>
      <PageHeader
        title="Team"
        subtitle="Invite colleagues to your organisation. They see your titles and numbers, and nobody else's."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="team-error">
          {error}
        </Alert>
      )}

      <Stack
        direction="row"
        gap={1}
        alignItems="flex-start"
        sx={{ mb: 1, maxWidth: 560 }}
      >
        <TextField
          size="small"
          label="Colleague's email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ flex: 1 }}
          inputProps={{ "data-testid": "team-invite-email" }}
          helperText="They can upload titles and see reach and revenue. Only Nenda can make someone a team admin."
        />
        <Button
          variant="contained"
          onClick={() => void invite()}
          disabled={!EMAIL.test(email.trim()) || sending}
          data-testid="team-invite-send"
        >
          Invite
        </Button>
      </Stack>
      {sentLink && (
        <Alert severity="success" sx={{ mb: 2, maxWidth: 560 }}>
          Invite sent. If the email does not arrive, share this link:{" "}
          <Box component="span" sx={{ wordBreak: "break-all" }}>
            {sentLink}
          </Box>
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <Stack gap={3} sx={{ mt: 2, maxWidth: 560 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>People</Typography>
            <Stack gap={0.5}>
              {members.map((m) => (
                <Stack
                  key={m.id}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  data-testid={`team-member-${m.email}`}
                  sx={{
                    p: 1,
                    border: `1px solid ${tokens.divider}`,
                    borderRadius: "8px",
                  }}
                >
                  <Typography sx={{ flex: 1, fontSize: 14 }}>
                    {m.email}
                  </Typography>
                  {m.isSelf && <Chip size="small" label="You" />}
                  {m.isTeamAdmin && <Chip size="small" label="Team admin" />}
                  {!m.isSelf && !m.isTeamAdmin && (
                    <Tooltip title="Remove from your organisation">
                      <IconButton
                        size="small"
                        aria-label={`Remove ${m.email}`}
                        onClick={() => void remove(m)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Waiting to accept
            </Typography>
            {invites.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                No open invites.
              </Typography>
            ) : (
              <Stack gap={0.5}>
                {invites.map((inv) => (
                  <Stack
                    key={inv.id}
                    direction="row"
                    alignItems="center"
                    gap={1}
                    data-testid={`team-invite-${inv.email}`}
                    sx={{
                      p: 1,
                      border: `1px dashed ${tokens.divider}`,
                      borderRadius: "8px",
                    }}
                  >
                    <Typography sx={{ flex: 1, fontSize: 14 }}>
                      {inv.email}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                      expires {new Date(inv.expiresAt).toLocaleDateString()}
                    </Typography>
                    <Tooltip title="Withdraw invite">
                      <IconButton
                        size="small"
                        aria-label={`Withdraw invite to ${inv.email}`}
                        onClick={() => void withdraw(inv)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
